import {
    CardEntries,
    CardEntry,
    ConvertedCurrencyValue,
    LocalizedDateTimeFromUtc,
    LocalizedSmallLabel,
    TypographyOptions,
} from '@mews-commander/ui';
import { Card, Stack } from '@optimus-web/core';
import { LocalizedEnumify } from '@mews/framework';
import { TextKey } from '@mews-commander/enums';
import { compactArray } from '@mews/fp';
import { Reservation } from '@mews-commander/data-access';
import { CancellationReason, ReservationPurpose } from '@mews-commander/legacy-data-structures';
import { getReservationGroupName } from '@mews-commander/legacy-entities/src/ducks';
import { isCurrentEnterpriseInMultiTaxEnvironment } from '@mews-commander/legacy-entities/src/selectors/session/enterprise';
import { getServicePerTimeUnitLocalization } from '@mews-commander/legacy-entities/src/utils/serviceTimeUnitLocalization';
import { getReservationPurchaseOrderNumber } from '@mews-commander/legacy-entities/src/ducks/reservationPurchaseOrderNumber';
import { useCommanderSelector } from '@mews-commander/utils';
import { ServiceName } from '../../../../components';
import BusinessSegmentName from '../reservationListItem/BusinessSegmentName';
import { CompanyLink, TravelAgencyLink } from '../Navigation';
import CommonReservationsDefaultProductOrderAverateAmount from '../reservationListItem/CommonReservationsDefaultProductOrderAverateAmount';
import ReservationsProductOrderOrderItemsAverageAmount from '../reservationListItem/ReservationsProductOrderOrderItemsAverageAmount';
import ReservationsTotalAmount from '../reservationListItem/ReservationsTotalAmount';
import { useAvailabilityBlock } from '../../../availabilityBlock/utils/useAvailabilityBlock';
import { VoucherName } from '../VoucherName';
import { AvailabilityBlockLinkName } from '../AvailabilityBlockLinkName';

interface ReservationDetailCardProps {
    reservation: Reservation;
}

const ReservationDetailCard = ({
    reservation: {
        businessSegmentId,
        availabilityBlockId,
        cancellationReason,
        purpose,
        preCancelTotalAmount,
        appliedVoucherId,
        partnerCompanyId,
        travelAgencyId,
        channelNumber,
        origin,
        originDetails,
        source,
        createdUtc,
        serviceId,
        id,
        groupId,
    },
}: ReservationDetailCardProps) => {
    const groupName = useCommanderSelector((state) => getReservationGroupName(state, { id: groupId }));
    const purchaseOrderNumber = useCommanderSelector((state) => getReservationPurchaseOrderNumber(state, { id }));

    const isMultiTaxEnvironment = useCommanderSelector<boolean>(isCurrentEnterpriseInMultiTaxEnvironment);

    const perTimeUnitLocalized = useCommanderSelector((state) =>
        getServicePerTimeUnitLocalization(state, { id: serviceId })
    );

    const availabilityBlock = useAvailabilityBlock(availabilityBlockId);

    const entries: Array<CardEntry | null> = [
        {
            textKey: TextKey.AverageTimeUnitRateShort,
            substitutes: { timeUnit: perTimeUnitLocalized },
            value: (
                <CommonReservationsDefaultProductOrderAverateAmount
                    serviceId={serviceId}
                    serviceOrderIds={[id]}
                    net={isMultiTaxEnvironment}
                />
            ),
        },
        {
            textKey: TextKey.AverageTimeUnitPriceWithProducts,
            substitutes: { timeUnit: perTimeUnitLocalized },
            value: (
                <ReservationsProductOrderOrderItemsAverageAmount
                    serviceId={serviceId}
                    serviceOrderIds={[id]}
                    net={isMultiTaxEnvironment}
                />
            ),
        },
        { textKey: TextKey.Service, value: <ServiceName id={serviceId} /> },
        availabilityBlock
            ? {
                  textKey: TextKey.AvailabilityBlock,
                  value: <AvailabilityBlockLinkName availabilityBlockId={availabilityBlock.id} />,
              }
            : null,

        appliedVoucherId != null ? { textKey: TextKey.Voucher, value: <VoucherName id={appliedVoucherId} /> } : null,
        partnerCompanyId != null ? { textKey: TextKey.Company, value: <CompanyLink id={partnerCompanyId} /> } : null,
        travelAgencyId != null
            ? { textKey: TextKey.TravelAgency, value: <TravelAgencyLink id={travelAgencyId} /> }
            : null,
        purchaseOrderNumber != null
            ? { textKey: TextKey.ReservationPurchaseOrderNumber, value: purchaseOrderNumber.value }
            : null,
        channelNumber != null ? { textKey: TextKey.TravelAgencyConfirmationNumber, value: channelNumber } : null,

        purpose != null
            ? {
                  textKey: TextKey.ReservationPurpose,
                  value: <LocalizedEnumify enumValue={ReservationPurpose.getByValue(purpose)} />,
              }
            : null,
        businessSegmentId != null
            ? { textKey: TextKey.Segment, value: <BusinessSegmentName businessSegmentId={businessSegmentId} /> }
            : null,

        { textKey: TextKey.Origin, value: origin },
        originDetails != null ? { textKey: TextKey.ReservationOriginDetails, value: originDetails } : null,
        source != null ? { textKey: TextKey.ReservationSource, value: source } : null,

        cancellationReason != null
            ? {
                  textKey: TextKey.CancellationReason,
                  value: <LocalizedEnumify enumValue={CancellationReason.getByName(cancellationReason)} />,
              }
            : null,
        preCancelTotalAmount
            ? {
                  textKey: TextKey.CanceledCost,
                  value: <ConvertedCurrencyValue amount={preCancelTotalAmount} net={isMultiTaxEnvironment} />,
              }
            : null,

        isMultiTaxEnvironment
            ? {
                  textKey: TextKey.TotalCostGross,
                  value: <ReservationsTotalAmount serviceOrderIds={[id]} net={false} />,
              }
            : null,
        groupName != null
            ? {
                  textKey: TextKey.GroupName,
                  value: <TypographyOptions wordBreak="break-all">{groupName}</TypographyOptions>,
              }
            : null,
        { textKey: TextKey.Created, value: <LocalizedDateTimeFromUtc utcString={createdUtc} /> },
    ];

    return (
        <Card overflowHidden padding="200" data-test-smart-detail-card="detail">
            <Stack vertical spacing="200">
                <LocalizedSmallLabel textKey={TextKey.DetailPlural} as="div" />

                <CardEntries entries={compactArray(entries)} />
            </Stack>
        </Card>
    );
};

export default ReservationDetailCard;
